import { useMemo } from 'react';
import { Check, Clock, FileCheck, UserX, CalendarX } from 'lucide-react';
import type { AttendanceStatus, KlassenbuchParticipant } from '../../lib/klassenbuchApi';

type Props = {
  participants: KlassenbuchParticipant[];
  disabled?: boolean;
  onSet: (studentKey: string, status: AttendanceStatus) => void;
  onOpenExcuse?: (participant: KlassenbuchParticipant) => void;
};

const STATUS_BUTTONS: {
  status: AttendanceStatus;
  label: string;
  activeClass: string;
  icon: React.ReactNode;
  title: string;
}[] = [
  {
    status: 'present',
    label: 'A',
    activeClass:
      'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/60 dark:border-emerald-800 dark:text-emerald-200',
    icon: <Check size={14} />,
    title: 'Anwesend'
  },
  {
    status: 'late',
    label: 'V',
    activeClass:
      'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/60 dark:border-amber-800 dark:text-amber-200',
    icon: <Clock size={14} />,
    title: 'Verspätet'
  },
  {
    status: 'excused',
    label: 'E',
    activeClass:
      'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200',
    icon: <FileCheck size={14} />,
    title: 'Entschuldigt'
  },
  {
    status: 'beurlaubt',
    label: 'B',
    activeClass:
      'bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-950/60 dark:border-purple-800 dark:text-purple-200',
    icon: <CalendarX size={14} />,
    title: 'Beurlaubt'
  },
  {
    status: 'absent',
    label: 'F',
    activeClass:
      'bg-rose-100 border-rose-300 text-rose-800 dark:bg-rose-950/60 dark:border-rose-800 dark:text-rose-200',
    icon: <UserX size={14} />,
    title: 'Fehlt (unentschuldigt)'
  }
];

function getStudentKey(p: KlassenbuchParticipant): string {
  return p.sophomorixUnid || p.Student_ID || p.username;
}

export default function AttendanceGrid({ participants, disabled, onSet, onOpenExcuse }: Props) {
  const items = useMemo(() => {
    return [...participants].sort((a, b) => {
      const an = `${a.last_name || a.Name || ''} ${a.first_name || a.Vorname || ''}`.trim();
      const bn = `${b.last_name || b.Name || ''} ${b.first_name || b.Vorname || ''}`.trim();
      return an.localeCompare(bn, 'de');
    });
  }, [participants]);

  if (!items.length) {
    return (
      <div className="p-4 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
        Keine Teilnehmerinnen oder Teilnehmer in dieser Stunde.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
            <th className="text-left py-2 pr-3">Schüler:in</th>
            <th className="text-left py-2 pr-3">Status</th>
            <th className="text-right py-2 pl-3">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const key = getStudentKey(p);
            const status = p.attendance_status;
            const hasActiveExcuse = (p.active_excuses || []).length > 0;
            return (
              <tr key={p.user_id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {p.last_name || p.Name || ''}{(p.last_name || p.Name) && (p.first_name || p.Vorname) ? ', ' : ''}{p.first_name || p.Vorname || p.username}
                  </div>
                  {hasActiveExcuse && (
                    <div className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                      Entschuldigung aktiv ({(p.active_excuses[0].kind)})
                    </div>
                  )}
                  {p.arrival_time && status === 'late' && (
                    <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">eingetroffen: {p.arrival_time}</div>
                  )}
                </td>
                <td className="py-2 pr-3">
                  <div className="flex flex-wrap gap-1">
                    {STATUS_BUTTONS.map((btn) => {
                      const active = status === btn.status;
                      return (
                        <button
                          key={btn.status}
                          type="button"
                          disabled={disabled}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium transition-colors ${
                            active
                              ? btn.activeClass
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                          title={btn.title}
                          onClick={() => onSet(key, btn.status)}
                        >
                          {btn.icon}
                          <span>{btn.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="py-2 pl-3 text-right">
                  {onOpenExcuse && (
                    <button
                      type="button"
                      disabled={disabled}
                      className="text-xs text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline"
                      onClick={() => onOpenExcuse(p)}
                    >
                      Entschuldigung/Beurlaubung
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-3">
        <span>A: Anwesend</span>
        <span>V: Verspätet</span>
        <span>E: Entschuldigt</span>
        <span>B: Beurlaubt</span>
        <span>F: Fehlt (unentschuldigt)</span>
      </div>
    </div>
  );
}
